import { ReportMetadataResponse } from "../../../../client";

export type StatusGroup =
  | "in_pipeline"
  | "blocked"
  | "cleared"
  | "in_progress"
  | "indicated"
  | "quoted"
  | "bound"
  | "lost"
  | "declined"
  | "not_cleared"
  | "closed";

export const STATUS_TO_GROUP: Record<
  ReportMetadataResponse["status"],
  StatusGroup
> = {
  pre_underwriting_review: "in_pipeline",
  blocked: "blocked",
  cleared: "cleared",
  pending: "in_progress",
  modeling_complete: "in_progress",
  referral_sent: "in_progress",
  flagged_for_review: "in_progress",
  indicated: "indicated",
  quoted: "quoted",
  quoted_not_taken_up: "lost",
  bound: "bound",
  issued: "bound",
  lost: "lost",
  declined: "declined",
  closed: "closed",
  closed_bor: "closed",
  not_reviewed: "closed",
  not_cleared_prior_submission: "not_cleared",
  not_cleared_incomplete_submission: "not_cleared",
  not_cleared_out_of_appetite: "not_cleared",
  not_cleared_effective_date_too_far: "not_cleared",
  not_cleared_unapproved_broker: "not_cleared",
  not_cleared_low_tiv: "not_cleared",
  not_cleared_foreign_exposure: "not_cleared",
};

// The analytics API types `status` as a plain string (the analytics view
// stores it as text). Look up the group tolerantly: unknown statuses return
// undefined and simply don't land in any funnel stage. STATUS_TO_GROUP stays
// keyed by the full status union so adding a status is still a compile error
// there until it's mapped.
export const statusGroup = (status: string): StatusGroup | undefined =>
  (STATUS_TO_GROUP as Record<string, StatusGroup>)[status];

export const TIV_BUCKETS: { label: string; min: number; max: number }[] = [
  { label: "<$25M", min: 0, max: 25_000_000 },
  { label: "$25–100M", min: 25_000_000, max: 100_000_000 },
  { label: "$100–250M", min: 100_000_000, max: 250_000_000 },
  { label: "$250M–1B", min: 250_000_000, max: 1_000_000_000 },
  { label: "$1–5B", min: 1_000_000_000, max: 5_000_000_000 },
  { label: "$5–20B", min: 5_000_000_000, max: 20_000_000_000 },
  { label: ">$20B", min: 20_000_000_000, max: Infinity },
];

export const LOCATION_BUCKETS: { label: string; min: number; max: number }[] = [
  { label: "1", min: 1, max: 1 },
  { label: "2–5", min: 2, max: 5 },
  { label: "6–25", min: 6, max: 25 },
  { label: "26–100", min: 26, max: 100 },
  { label: "101–500", min: 101, max: 500 },
  { label: "501–5000", min: 501, max: 5000 },
  { label: ">5000", min: 5001, max: Infinity },
];

// SIC 2-digit major-group descriptions. Compact phrasing tailored to
// the histogram label width — full SIC titles are 60+ chars.
export const SIC2_LABELS: Record<string, string> = {
  "01": "Crop production",
  "02": "Livestock production",
  "07": "Agricultural services",
  "08": "Forestry",
  "09": "Fishing & hunting",
  "10": "Metal mining",
  "12": "Coal mining",
  "13": "Oil & gas extraction",
  "14": "Nonmetallic mining",
  "15": "Building construction",
  "16": "Heavy construction",
  "17": "Special-trade contractors",
  "20": "Food manufacturing",
  "21": "Tobacco products",
  "22": "Textile mills",
  "23": "Apparel manufacturing",
  "24": "Lumber & wood products",
  "25": "Furniture & fixtures",
  "26": "Paper & allied products",
  "27": "Printing & publishing",
  "28": "Chemicals",
  "29": "Petroleum refining",
  "30": "Rubber & plastics",
  "31": "Leather products",
  "32": "Stone, clay, glass, concrete",
  "33": "Primary metals",
  "34": "Fabricated metal products",
  "35": "Industrial machinery",
  "36": "Electronic & electrical equipment",
  "37": "Transportation equipment",
  "38": "Measuring & medical instruments",
  "39": "Misc. manufacturing",
  "40": "Railroad transportation",
  "41": "Local transit",
  "42": "Motor freight",
  "43": "US Postal Service",
  "44": "Water transportation",
  "45": "Air transportation",
  "46": "Pipelines",
  "47": "Transportation services",
  "48": "Communications",
  "49": "Electric, gas, sanitary",
  "50": "Wholesale — durable",
  "51": "Wholesale — nondurable",
  "52": "Building materials & garden",
  "53": "General merchandise stores",
  "54": "Food stores",
  "55": "Auto dealers & gas stations",
  "56": "Apparel stores",
  "57": "Home furniture & furnishings",
  "58": "Eating & drinking places",
  "59": "Misc. retail",
  "60": "Depository institutions",
  "61": "Non-depository credit",
  "62": "Securities & commodities",
  "63": "Insurance carriers",
  "64": "Insurance agents & brokers",
  "65": "Real estate",
  "67": "Holding & investment offices",
  "70": "Hotels & lodging",
  "72": "Personal services",
  "73": "Business services",
  "75": "Auto repair & parking",
  "76": "Misc. repair services",
  "78": "Motion pictures",
  "79": "Amusement & recreation",
  "80": "Health services",
  "81": "Legal services",
  "82": "Educational services",
  "83": "Social services",
  "84": "Museums & galleries",
  "86": "Membership organizations",
  "87": "Engineering & accounting",
  "88": "Private households",
  "89": "Misc. services",
  "91": "Executive & legislative gov",
  "92": "Justice & public safety",
  "93": "Public finance",
  "94": "Human resource programs",
  "95": "Environmental & housing programs",
  "96": "Economic programs",
  "97": "National security & intl affairs",
  "99": "Nonclassifiable",
};

export const sic2Label = (code: string): string => {
  const desc = SIC2_LABELS[code];
  return desc ? `${code} · ${desc}` : code;
};

// ISO construction class labels. The address.construction_code field can
// hold codes from multiple systems (ISO 1-6, ATC, RMS); we only label the
// ISO ones here. Anything else falls through to the raw code.
export const ISO_CONSTRUCTION_LABELS: Record<string, string> = {
  "1": "Frame",
  "2": "Joisted Masonry",
  "3": "Noncombustible",
  "4": "Masonry Noncombustible",
  "5": "Modified Fire Resistive",
  "6": "Fire Resistive",
};

export const constructionLabel = (code: string): string => {
  const desc = ISO_CONSTRUCTION_LABELS[code];
  return desc ? `${code} · ${desc}` : code;
};

// Collapse the tail of a histogram into a single "Other" bucket once we
// have more than `n` rows. Buckets must already be sorted descending by
// count — every builder in SubmissionAnalytics.tsx does that. Returns the
// input untouched when there's nothing to collapse.
export type HistogramBucket = { label: string; count: number; bound: number };

export const topNWithOther = (
  buckets: HistogramBucket[],
  n: number
): HistogramBucket[] => {
  if (buckets.length <= n) return buckets;
  const top = buckets.slice(0, n);
  const rest = buckets.slice(n);
  const other: HistogramBucket = {
    label: `Other (${rest.length})`,
    count: rest.reduce((s, b) => s + b.count, 0),
    bound: rest.reduce((s, b) => s + b.bound, 0),
  };
  return [...top, other];
};

// Format a non-negative percentage (already scaled to 0–100) with no
// decimals. A true 0% prints as "0%", but a value that rounds down to 0
// while being strictly positive prints as "<1%" so the reader can tell
// "none" from "a tiny share". Use for conversion/bind rates; signed
// deltas should format themselves.
export const formatPct = (pct: number): string => {
  if (pct === 0) return "0%";
  if (pct > 0 && pct < 0.5) return "<1%";
  return `${pct.toFixed(0)}%`;
};

export type DateWindow = "all" | "ytd" | "12mo" | "90d" | "30d";

export const dateWindowStart = (w: DateWindow): Date | null => {
  const now = new Date();
  switch (w) {
    case "all":
      return null;
    case "ytd":
      return new Date(now.getFullYear(), 0, 1);
    case "12mo": {
      const d = new Date(now);
      d.setFullYear(d.getFullYear() - 1);
      return d;
    }
    case "90d": {
      const d = new Date(now);
      d.setDate(d.getDate() - 90);
      return d;
    }
    case "30d": {
      const d = new Date(now);
      d.setDate(d.getDate() - 30);
      return d;
    }
  }
};
